import React, { useState, useEffect } from "react";
import AdminSidebar from "../../components/AdminSidebar";
import { Database, Search, Trash2, Eye, Edit, Menu } from "lucide-react";
import {
  fetchCustomModels,
  updateModelStatus,
  deleteCustomModel,
} from "../../../services/modelApi";
import { toast } from "react-toastify";
import { ConfirmationModal } from "../../components/ConfirmationModel";
import { useNavigate } from "react-router-dom";
import { Drawer } from "../../components/ui/Drawer";

const ModelListing = () => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [updatingModel, setUpdatingModel] = useState(null);
  const navigate = useNavigate();

  // Modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modelToDelete, setModelToDelete] = useState(null);

  useEffect(() => {
    fetchModels();
  }, []);

  const fetchModels = async () => {
    setLoading(true);
    try {
      const response = await fetchCustomModels();
      setModels(
        response.data.filter((d) => d._id !== "68d36b6d46000e7dfcf7e8ad")
      );
    } catch (error) {
      console.error("Error fetching models:", error);
      toast.error("Failed to fetch bots");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (modelId, newStatus) => {
    setUpdatingModel(modelId);
    try {
      await updateModelStatus(modelId, newStatus);
      toast.success("Bot status updated successfully");
      fetchModels(); // Refresh the list
    } catch (error) {
      console.error("Error updating model status:", error);
      toast.error(error.message || "Failed to update bot status");
    } finally {
      setUpdatingModel(null);
    }
  };

  // Open delete confirmation modal
  const handleDeleteClick = (model) => {
    setModelToDelete(model);
    setShowDeleteModal(true);
  };

  // Handle confirmed deletion
  const handleConfirmDelete = async () => {
    if (!modelToDelete) return;

    setUpdatingModel(modelToDelete._id);
    setShowDeleteModal(false);

    try {
      setModels((prevModels) =>
        prevModels.filter((model) => model._id !== modelToDelete._id)
      );
      await deleteCustomModel(modelToDelete._id);
      toast.success("Bot deleted successfully");
    } catch (error) {
      console.error("Error deleting model:", error);
      toast.error(error.message || "Failed to delete bot");
    } finally {
      setUpdatingModel(null);
      setModelToDelete(null);
    }
  };

  // Close modal
  const handleCloseModal = () => {
    setShowDeleteModal(false);
    setModelToDelete(null);
  };

  // Handle edit model
  const handleEdit = (model) => {
    navigate(`/admin-dashboard/model-edit/${model._id}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "bg-green-500/20 text-green-400";
      case "inactive":
        return "bg-gray-500/20 text-gray-400";
      default:
        return "bg-gray-500/20 text-gray-400";
    }
  };

  const getStatusDot = (status) => {
    switch (status) {
      case "active":
        return "bg-green-400";
      case "inactive":
        return "bg-gray-500";
      default:
        return "bg-gray-500";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const filteredModels = models.filter(
    (model) =>
      model.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      model.modelHandle?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const [showSidebar, setShowSidebar] = useState(false);
  const onClose = () => {
    setShowSidebar(false);
  };
  return (
    <div className="w-screen min-h-screen  bg-gray-800 overflow-x-hidden">
      <div className="flex min-h-screen">
        {/* Sidebar */}
        <div className="hidden lg:block w-64 h-full">
          <AdminSidebar />
        </div>
        <Drawer
          height="full"
          title={"Admin"}
          isOpen={showSidebar}
          onClose={onClose}
        >
          <div className=" lg:hidden  w-full h-full">
            <AdminSidebar />
          </div>
        </Drawer>
        {/* Main Content */}
        <div className="flex-1 flex flex-col h-full overflow-y-auto bg-gray-800">
          {/* Header */}
          <div className="px-3 lg:px-8 py-6 border-b gap-2 border-gray-700 flex justify-between items-start">
            <div className="flex  lg:w-full flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center gap-3 mb-2 md:mb-0">
                <Database className="text-blue-400" size={24} />
                <h1 className="text-2xl font-semibold text-white">
                  Bot Listing
                </h1>
              </div>
              <div className="flex gap-4 items-center">
                <div className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 flex items-center gap-2">
                  <span className="font-semibold text-white text-sm">
                    {models.length}
                  </span>
                  <span className="text-gray-300 text-sm">Bots</span>
                </div>
                <div className="relative">
                  <input
                    type="text"
                    className="pl-10 pr-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Search bots..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Search
                    className="absolute left-3 top-2.5 text-gray-400"
                    size={16}
                  />
                </div>
              </div>
            </div>
            <div
              onClick={() => {
                setShowSidebar(true);
              }}
              className="max-w-max lg:hidden"
            >
              <Menu size={30} color="white" />
            </div>
          </div>

          {/* Table Content */}
          <div className="flex-1 p-2 lg:p-8">
            <div className="bg-gray-900 border border-gray-700 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-700">
                  <thead className="bg-gray-800">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                        Bot Name
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                        Handle
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                        Created At
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                        Internet Access
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-gray-900 divide-y divide-gray-700">
                    {loading ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-12 text-center">
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
                            <span className="ml-2 text-gray-400">
                              Loading bots...
                            </span>
                          </div>
                        </td>
                      </tr>
                    ) : filteredModels.length === 0 ? (
                      <tr>
                        <td
                          colSpan="6"
                          className="px-6 py-12 text-center text-gray-400"
                        >
                          {searchTerm
                            ? "No bots found matching your search"
                            : "No bots found"}
                        </td>
                      </tr>
                    ) : (
                      filteredModels.map((model) => (
                        <tr
                          key={model._id}
                          className="hover:bg-gray-800 transition-colors"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div
                                className={`w-2 h-2 rounded-full ${getStatusDot(
                                  model.status || "active"
                                )} mr-3`}
                              ></div>
                              <span className="font-medium text-white text-sm">
                                {model.name}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-300 text-sm font-mono">
                            {model.modelHandle}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-300 text-sm">
                            {formatDate(model.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <select
                              className={`px-3 py-1 rounded-full text-xs font-medium border-0 bg-transparent ${getStatusColor(
                                model.status || "active"
                              )}`}
                              value={model.status || "active"}
                              onChange={(e) =>
                                handleStatusChange(model._id, e.target.value)
                              }
                              disabled={updatingModel === model._id}
                            >
                              <option value="active" className="bg-gray-800">
                                Active
                              </option>
                              <option value="inactive" className="bg-gray-800">
                                Inactive
                              </option>
                            </select>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              {model.internetAccess ? (
                                <>
                                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                  <span className="text-xs text-green-400 font-medium">
                                    Enabled
                                  </span>
                                </>
                              ) : (
                                <>
                                  <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                                  <span className="text-xs text-gray-400 font-medium">
                                    Disabled
                                  </span>
                                </>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              {updatingModel === model._id ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400"></div>
                              ) : (
                                <>
                                  <button
                                    onClick={() => handleDeleteClick(model)}
                                    className="text-red-400 hover:text-red-300 transition-colors"
                                    title="Delete Bot"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                  <button
                                    onClick={() => handleEdit(model)}
                                    className="text-blue-400 hover:text-blue-300 transition-colors"
                                    title="Edit Bot"
                                  >
                                    <Edit size={16} />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDelete}
        title="Delete Bot"
        message={`Are you sure you want to delete bot "${modelToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isDestructive={true}
      />
    </div>
  );
};

export default ModelListing;
